/**
 * The ONE animation loop shared by every <Mascot>.
 *
 * A page can show many faces (an agent list, a chat header, the hero). Each one
 * owning its own requestAnimationFrame loop and its own window `pointermove`
 * listener scales linearly with the row count; instead every visible, animated
 * face subscribes a per-frame update here. There is one rAF loop while at
 * least one face subscribes, and one pointer listener while at least one
 * pointer-following (`level="full"`) face does; none after the last leaves. Faces that scroll offscreen unsubscribe (see `observeVisibility`),
 * and rAF itself stops in a hidden tab.
 */

export type FrameFn = (now: number) => void;

/** Last pointer position in viewport coordinates; `t` is its `performance.now()` stamp. */
export const pointer = { x: 0, y: 0, t: Number.NEGATIVE_INFINITY };

const subscribers = new Set<FrameFn>();
let rafId = 0;
let pointerSubscribers = 0;

const onPointerMove = (e: PointerEvent) => {
  pointer.x = e.clientX;
  pointer.y = e.clientY;
  pointer.t = performance.now();
};

const loop = (now: number) => {
  subscribers.forEach((fn) => fn(now));
  rafId = subscribers.size > 0 ? requestAnimationFrame(loop) : 0;
};

/**
 * Run `fn` on every animation frame until the returned function is called.
 * `followPointer` keeps `pointer` updated while this subscription lives.
 */
export function subscribeFrame(fn: FrameFn, followPointer = false): () => void {
  subscribers.add(fn);
  if (subscribers.size === 1) rafId = requestAnimationFrame(loop);
  if (followPointer && ++pointerSubscribers === 1) {
    window.addEventListener('pointermove', onPointerMove, { passive: true });
  }
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    subscribers.delete(fn);
    if (followPointer && --pointerSubscribers === 0) {
      window.removeEventListener('pointermove', onPointerMove);
    }
    if (subscribers.size === 0) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };
}

let observer: IntersectionObserver | null = null;
const visibilityCallbacks = new WeakMap<Element, (visible: boolean) => void>();

/**
 * Report whether `el` is on screen, through one shared IntersectionObserver.
 * Where IntersectionObserver does not exist the element counts as visible.
 */
export function observeVisibility(el: Element, onChange: (visible: boolean) => void): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    onChange(true);
    return () => undefined;
  }
  observer ??= new IntersectionObserver((entries) => {
    entries.forEach((entry) => visibilityCallbacks.get(entry.target)?.(entry.isIntersecting));
  });
  visibilityCallbacks.set(el, onChange);
  observer.observe(el);
  return () => {
    observer?.unobserve(el);
    visibilityCallbacks.delete(el);
  };
}
